import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { mergeObjects } from "@utils:object";
import { ReactNode } from "react";
import { IoMdClose } from "react-icons/io";

export interface SuperDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  titlePlacement?: "start" | "center";
  children: ReactNode;
  sx?: SxProps<Theme>;
  actionControls?: {
    primary?: {
      label: string;
      onClick?: () => void;
      disabled?: boolean;
      sx?: SxProps<Theme>;
    };
    secondary?: {
      label: string;
      onClick?: () => void;
      disabled?: boolean;
      sx?: SxProps<Theme>;
    };
  };
  slotProps?: {
    dialogContent?: {
      sx?: SxProps<Theme>;
    };
  };
  isLoading?: boolean;
}

export const SuperDialog = ({
  open,
  onClose,
  title,
  children,
  sx,
  actionControls,
  isLoading,
  titlePlacement = "center",
  slotProps,
}: SuperDialogProps) => {
  return (
    <Dialog
      className={`SuperDialog__${title}`}
      sx={mergeObjects(
        {
          "& .MuiDialog-paper": {
            maxHeight: "80vh",
            maxWidth: 900,
            overflow: "auto",
            borderRadius: "0.8rem",
          },
        },
        sx,
      )}
      maxWidth="xs"
      open={open}
    >
      <DialogTitle
        sx={{
          fontWeight: "500",
          fontSize: "20px",
          lineHeight: "18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E0E0E0",

          "&.MuiDialogTitle-root": {
            paddingY: "10px",
          },
        }}
      >
        <Typography
          sx={{
            ...(titlePlacement === "center" && {
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }),
            fontWeight: "500",
            fontSize: "1.1rem",
          }}
        >
          {title}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onClose()}
          sx={{
            marginLeft: "auto",
          }}
        >
          <IoMdClose />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          paddingTop: "2rem !important",
          ...(isLoading && {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }),
          ...slotProps?.dialogContent?.sx,
        }}
      >
        {isLoading ? <CircularProgress /> : children}
      </DialogContent>
      {(actionControls?.secondary || actionControls?.primary) && (
        <DialogActions
          sx={{
            padding: 3,
            paddingTop: 0,
          }}
        >
          {actionControls?.secondary && (
            <Button
              sx={actionControls?.secondary.sx}
              autoFocus
              onClick={() => actionControls?.secondary?.onClick?.()}
              variant="outlined"
              disabled={actionControls?.secondary?.disabled}
            >
              {actionControls?.secondary.label}
            </Button>
          )}
          {actionControls?.primary && (
            <Button
              sx={actionControls?.primary.sx}
              onClick={() => actionControls?.primary?.onClick?.()}
              variant="contained"
              disabled={actionControls?.primary?.disabled}
            >
              {actionControls?.primary.label}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};
